import { Flex, HStack, Input, Text } from '@chakra-ui/react';
import React from 'react';

import { colors } from 'packages/common-frontend/colors';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import {
  CreditAgreement,
  CreditAgreementFullArtifactWipConfigType,
} from 'packages/creditAgreements/ui/types';

type FacilityProfileField = {
  key: CreditAgreement.DomainEntity.FacilityProfileKey;
  help: string;
};

const FIELDS: readonly FacilityProfileField[] = [
  {
    key: 'assetClass',
    help: 'What this deal finances, e.g. "Equipment lease receivables".',
  },
  {
    key: 'primaryBalanceField',
    help:
      'The servicer’s name for the balance its tape reports, e.g. "Ending Net Investment Amount".',
  },
];

type FacilityProfileProps = {
  /** Absent until the deal's profile is first set. */
  backupServicingMechanics:
    | CreditAgreement.DomainEntity.BackupServicingMechanics
    | undefined;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
};

/**
 * The deal's descriptive profile — asset class and primary balance label — as
 * two plain text inputs over the CA's `backupServicingMechanics`. These are
 * config rather than agreement terms, so they skip the property table's
 * section / note / written-in columns. Clearing an input removes the field.
 */
export const FacilityProfile = ({
  backupServicingMechanics,
  wipConfig,
}: FacilityProfileProps): React.ReactElement => {
  const basePropertyKey = 'backupServicingMechanics';
  const isEditable = wipConfig?.isMutateAllowed(basePropertyKey) ?? false;

  return (
    <StickyHeaderSection labelText="Deal Profile">
      <Flex direction="column" gap="1rem" mb="2rem">
        {FIELDS.map(({ key, help }) => {
          const value = backupServicingMechanics?.[key];
          return (
            <HStack key={key} width="full" alignItems="flex-start">
              <Text fontSize="md" width="25%" pt={isEditable ? '0.5rem' : 0}>
                {CreditAgreement.DomainEntity.FacilityProfilePropertyNames[key]}
              </Text>
              <Flex direction="column" width="full" gap="0.25rem">
                {isEditable && wipConfig ? (
                  <Input
                    data-testid={`FacilityProfile.${key}`}
                    value={value ?? ''}
                    onChange={(e): void => {
                      // The promise only carries validation, which Formik also
                      // surfaces through `errors`; nothing to handle here.
                      wipConfig.formProps
                        .setFieldValue(
                          `${basePropertyKey}.${key}`,
                          e.target.value === '' ? undefined : e.target.value,
                        )
                        .catch((): void => undefined);
                    }}
                  />
                ) : (
                  <Text fontSize="md">{value ?? '—'}</Text>
                )}
                <Text fontSize="sm" color={colors.gray[500]}>
                  {help}
                </Text>
              </Flex>
            </HStack>
          );
        })}
      </Flex>
    </StickyHeaderSection>
  );
};
